import React, { useEffect, useMemo, useState } from "react";
import { useGame } from "../game/em2c-store";
import { EM2C_BUILDINGS } from "../game/data/em2c-buildings";
import {
  ALERT_LEVELS, COLONY_POLICIES,
} from "../game/systems/ColonyResourceController";
import { Zap, Battery, AlertTriangle, ChevronDown, LocateFixed } from "lucide-react";

export default function ColonyAlertBar() {
  const controllerState = useGame((s) => s._colonyControllerState);
  const colonyAlert = useGame((s) => s._colonyAlert);
  const colonyPolicy = useGame((s) => s.colonyPolicy);
  const setColonyPolicy = useGame((s) => s.setColonyPolicy);
  const buildings = useGame((s) => s.buildings);
  const selectBuilding = useGame((s) => s.selectBuilding);
  const [expanded, setExpanded] = useState(false);
  const [shedOpen, setShedOpen] = useState(false);
  const [shedHover, setShedHover] = useState(false);

  useEffect(() => {
    const close = () => {
      setExpanded(false);
      setShedOpen(false);
      setShedHover(false);
    };
    window.addEventListener("mars:world-click", close);
    return () => window.removeEventListener("mars:world-click", close);
  }, []);

  const shedBuildings = useMemo(() => {
    if (!controllerState) return [];
    const source = controllerState.shedBuildings;
    const ids = source instanceof Set
      ? [...source]
      : Array.isArray(source)
        ? source
        : source && typeof source === "object"
          ? Object.keys(source).filter((id) => source[id])
          : [];

    return ids
      .map((id) => buildings.find((building) => building.id === id))
      .filter(Boolean)
      .map((building) => ({
        ...building,
        displayName: EM2C_BUILDINGS[building.type]?.name || building.type || "Unknown Building",
      }));
  }, [controllerState, buildings]);

  if (!controllerState) return null;

  const {
    powerProduction, powerConsumption, netPower,
    batteryPct, batteryReserveHours,
    stormIncoming, stormHoursAway, stormEnergyDeficit,
    shedCount,
  } = controllerState;

  const alert = colonyAlert || ALERT_LEVELS.NORMAL;
  const alertColor = alert.color;
  const totalShed = shedBuildings.length || Object.values(shedCount || {}).reduce((a, b) => a + b, 0);
  // Reserve time only makes sense while the *actual* colony power balance is
  // negative. Keeping a fixed-width slot prevents the alert row from jumping.
  const showReserve = netPower < -0.01 && batteryReserveHours != null;

  const focusBuilding = (building) => {
    if (!building) return;
    selectBuilding?.(building.id);
    setShedOpen(false);
    setShedHover(false);
    window.dispatchEvent(new CustomEvent("mars:focus-building", {
      detail: {
        id: building.id,
        q: building.q,
        r: building.r,
        x: building.x,
        z: building.z,
      },
    }));
  };

  if (alert.id === "NORMAL" && !stormIncoming && totalShed === 0) {
    return (
      <div className="absolute top-14 right-3 z-25 pointer-events-auto" style={{ fontFamily: "Rajdhani, sans-serif" }}>
        <PolicySelector colonyPolicy={colonyPolicy} setColonyPolicy={setColonyPolicy} expanded={expanded} setExpanded={setExpanded} />
      </div>
    );
  }

  return (
    <div className="absolute top-14 left-0 right-0 z-25 pointer-events-auto" style={{ fontFamily: "Rajdhani, sans-serif" }}>
      <div
        className="h-10 flex items-center gap-3 px-4 border-b backdrop-blur-md whitespace-nowrap overflow-visible"
        style={{
          background: `linear-gradient(90deg, ${alertColor}15, ${alertColor}05, ${alertColor}15)`,
          borderColor: `${alertColor}40`,
        }}
      >
        <div className="flex items-center gap-2 shrink-0 min-w-[8.5rem]">
          <AlertTriangle size={16} style={{ color: alertColor }} />
          <span className="text-xs font-mono uppercase tracking-widest font-bold" style={{ color: alertColor }}>{alert.label}</span>
        </div>

        <div className="w-px h-4 bg-white/10 shrink-0" />

        <div className="flex items-center gap-2 shrink-0 min-w-[13.5rem]">
          <Zap size={15} className="text-white" />
          <span className="font-mono text-[11px] text-white">
            {powerProduction.toFixed(0)} <span className="text-slate-500">/</span> {powerConsumption.toFixed(0)} kW
          </span>
          <span className="font-mono text-[10px] inline-block min-w-[4.8rem]" style={{ color: netPower >= 0 ? "#00fa9a" : "#ff3366" }}>
            ({netPower >= 0 ? "+" : ""}{netPower.toFixed(1)})
          </span>
        </div>

        <div className="w-px h-4 bg-white/10 shrink-0" />

        <div className="flex items-center gap-2 shrink-0 min-w-[16rem]">
          <Battery size={15} className="text-white" />
          <div className="relative w-16 h-2 bg-white/10 rounded-sm overflow-hidden shrink-0">
            <div
              className="h-full transition-[width] duration-500 rounded-sm"
              style={{ width: `${Math.min(100, batteryPct)}%`, background: alertColor, boxShadow: `0 0 6px ${alertColor}` }}
            />
          </div>
          <span className="font-mono text-[10px] text-white w-10">{batteryPct.toFixed(0)}%</span>
          <span className="font-mono text-[10px] text-slate-400 inline-block w-24">
            {showReserve ? `· ${batteryReserveHours.toFixed(1)}h reserve` : ""}
          </span>
        </div>

        {stormIncoming && (
          <>
            <div className="w-px h-4 bg-white/10 shrink-0" />
            <div className="flex items-center gap-1 px-2 py-0.5 border border-[#ff8c00]/40 bg-[#ff8c00]/10 shrink-0 min-w-[13rem]">
              <span className="text-[10px] font-mono uppercase text-[#ff8c00]">Storm in {stormHoursAway?.toFixed(1)}h</span>
              {stormEnergyDeficit > 0 && <span className="text-[10px] font-mono text-[#ff3366]">· -{stormEnergyDeficit.toFixed(0)} kWh deficit</span>}
            </div>
          </>
        )}

        {totalShed > 0 && (
          <>
            <div className="w-px h-4 bg-white/10 shrink-0" />
            <div
              className="relative shrink-0"
              onMouseEnter={() => setShedHover(true)}
              onMouseLeave={() => setShedHover(false)}
            >
              <button
                type="button"
                onClick={() => setShedOpen((value) => !value)}
                className="h-8 min-w-[9.5rem] px-2 flex items-center gap-1.5 border border-[#ff8c00]/25 bg-[#ff8c00]/[0.04] text-[#ff8c00] hover:bg-[#ff8c00]/10 hover:border-[#ff8c00]/50 transition-colors"
                data-testid="shed-buildings-button"
              >
                <AlertTriangle size={14} />
                <span className="font-mono text-[10px]">{totalShed} building{totalShed > 1 ? "s" : ""} shed</span>
                <ChevronDown size={11} className={`ml-auto transition-transform ${shedOpen ? "rotate-180" : ""}`} />
              </button>

              {shedHover && !shedOpen && (
                <div className="absolute left-0 top-full mt-1 w-64 bg-[#0A0C10]/98 border border-[#ff8c00]/30 shadow-xl p-2 z-[90]">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#ff8c00] mb-1.5">Power-shed buildings</div>
                  {shedBuildings.length > 0 ? shedBuildings.map((building) => (
                    <div key={building.id} className="text-[10px] font-mono text-slate-300 py-0.5 truncate">• {building.displayName}</div>
                  )) : (
                    <div className="text-[10px] font-mono text-slate-500">Controller is updating affected building details…</div>
                  )}
                  <div className="mt-1.5 pt-1.5 border-t border-white/10 text-[9px] font-mono text-slate-500">Click to open navigation list</div>
                </div>
              )}

              {shedOpen && (
                <div className="absolute left-0 top-full mt-1 w-72 bg-[#0A0C10]/98 border border-[#ff8c00]/35 shadow-[0_12px_30px_rgba(0,0,0,0.6)] z-[95] overflow-hidden" data-testid="shed-buildings-menu">
                  <div className="px-3 py-2 border-b border-white/10">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-[#ff8c00]">Affected Buildings</div>
                    <div className="text-[9px] font-mono text-slate-500 mt-0.5">Select a building to locate it on the map</div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {shedBuildings.map((building) => (
                      <button
                        key={building.id}
                        type="button"
                        onClick={() => focusBuilding(building)}
                        className="w-full px-3 py-2 flex items-center gap-2 text-left border-b border-white/[0.06] hover:bg-[#ff8c00]/10 transition-colors"
                        data-testid={`shed-building-${building.id}`}
                      >
                        <LocateFixed size={15} className="text-[#ff8c00] shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-white truncate">{building.displayName}</div>
                          <div className="text-[9px] font-mono text-slate-500 truncate">{building.controllerReason || "Automatic load shedding"}</div>
                        </div>
                        <span className="text-[9px] font-mono text-[#ff8c00]">LOCATE</span>
                      </button>
                    ))}
                    {shedBuildings.length === 0 && (
                      <div className="px-3 py-3 text-[10px] font-mono text-slate-500">Affected building details are updating.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div className="ml-auto shrink-0">
          <PolicySelector colonyPolicy={colonyPolicy} setColonyPolicy={setColonyPolicy} expanded={expanded} setExpanded={setExpanded} />
        </div>
      </div>
    </div>
  );
}

function PolicySelector({ colonyPolicy, setColonyPolicy, expanded, setExpanded }) {
  const policies = Object.values(COLONY_POLICIES);
  return (
    <div className="relative">
      <button onClick={() => setExpanded(!expanded)} className="h-8 flex items-center gap-1.5 px-2 border border-white/15 bg-white/5 hover:bg-white/10 text-white">
        <span className="text-[10px] font-mono uppercase tracking-widest">{COLONY_POLICIES[colonyPolicy]?.label || "Balanced"}</span>
        <ChevronDown size={10} className={expanded ? "rotate-180" : ""} />
      </button>
      {expanded && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-[#0A0C10]/98 border border-white/15 backdrop-blur-md shadow-lg z-50">
          {policies.map((p) => (
            <button
              key={p.id}
              onClick={() => { setColonyPolicy(p.id); setExpanded(false); }}
              className={`w-full text-left px-3 py-2 text-[11px] font-mono uppercase tracking-widest hover:bg-white/5 ${colonyPolicy === p.id ? "text-cyan-400 bg-cyan-500/10" : "text-slate-300"}`}
            >
              {p.label}
              <div className="text-[9px] text-slate-500 normal-case mt-0.5">
                {p.id === "SURVIVAL" && "Aggressive saving, lower thresholds"}
                {p.id === "BALANCED" && "Automatic — recommended"}
                {p.id === "MAX_PRODUCTION" && "Tolerate lower reserves"}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
